/* ============================================================
   ページ遷移アニメーション 制御スクリプト（fetchなし・サイト全体版）
   ------------------------------------------------------------
   仕様（クライアント要望より）:
     1. 通常の表示・リロード時はイントロ（幕）を再生する。
        ただしTOP内のロゴクリックでは再読み込みせず、白いフェードで先頭へ戻す。
     2. ページ遷移（内部リンク）ではイントロを表示しないが、
        下層ページ → TOPページ の遷移時のみ、幕とコンテンツが連動する
        アニメーションを再生する

   実装方針:
     fetch()やコンテンツの複製は使わない。ページ遷移は常に本物の
     フルリロードのまま起こし（＝各種アプリ・計測タグは通常通り
     動作する）、見た目のアニメーションだけを2つのページにまたがる
     形で「連動して見える」ように繋げる。

       [下層ページ側] 「TOPへ戻る」リンクをクリック
         → 現在のページのコンテンツ(.js-page-content)を、
           幕が上から入ってくるのに合わせてその場で下へ押し出す
         → 幕が画面を覆い切ったタイミングで実際に遷移する
           （sessionStorageに「次はTOPの入場演出をする」フラグを立てる）

       [TOPページ側] 上記のフラグを持って読み込まれた場合
         → 幕がすでに画面を覆った状態から始まり、幕が下へ抜けながら
           コンテンツが上から押し込まれてくる演出を再生する

       [それ以外の内部リンク（TOP→下層など）]
         → 何もしない。普通のリンクとして遷移させる。
           ただし遷移先では sessionStorage の「イントロを出さない」
           フラグにより、[条件1]の幕も再生されないようにする。

       [直接アクセス／リロード／フラグなし]
         → [条件1] のシンプルな幕（コンテンツは動かない）を再生する。

     サイト全体に適用するため、個別リンクへの data-nav 属性付けは
     不要。document全体へのクリック委譲で、リンク先がサイトの
     TOPページ（"/"）かどうかをURLから自動判定する。

     必須のマークアップ規約（テーマ側で用意するもの）:
       - <div class="curtain" hidden> ... </div> をレイアウト内に1つ
       - メインコンテンツを囲う要素に class="js-page-content" を付与
       - <head> 内、CSS読み込みより前に、後述の同期スクリプトを設置
         （FOUC防止のため。index.html / contact.html を参照）
   ============================================================ */

(function () {
  "use strict";

  var SKIP_FLAG = "curtainSkipNext"; // 次のページでイントロを出さない
  var ENTER_FLAG = "curtainEnterNext"; // 次のページ(TOP)で入場演出をする
  var FORCE_TOP_FLAG = "curtainForceTop"; // 次のページで先頭スクロールを強制する

  var html = document.documentElement;

  function safeGet(key) {
    try {
      return sessionStorage.getItem(key);
    } catch (e) {
      return null;
    }
  }

  function safeSet(key, value) {
    try {
      if (value === null) {
        sessionStorage.removeItem(key);
      } else {
        sessionStorage.setItem(key, value);
      }
    } catch (e) {
      /* 保存できない環境では何もしない（安全側＝毎回イントロが再生される） */
    }
  }

  function reduceMotion() {
    return (
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    );
  }

  /* 初期状態を確実に1フレーム描画させてから次のフレームで
     アニメーション先の値を適用する（ダブルrAF）。
     「offsetHeightで強制リフロー→同じフレーム内で値変更」よりも
     確実にトランジションの開始点を描画させられる。

     ただし requestAnimationFrame はタブが非アクティブ（フォーカス外・
     非表示）な場合、ブラウザによって大幅に遅延・間引きされることが
     ある。その間に setTimeout ベースの他の処理（実際のページ遷移など）
     だけが先に進んでしまうと、幕のアニメーションが一切描画されない
     まま遷移してしまう不具合につながる。
     そのため setTimeout によるフォールバックを必ず併走させ、
     どちらか早く発火した方でコールバックを実行する。 */
  function nextFrame(callback) {
    var done = false;
    var run = function () {
      if (done) return;
      done = true;
      callback();
    };
    requestAnimationFrame(function () {
      requestAnimationFrame(run);
    });
    // rAFが遅延・間引きされる環境向けの保険（体感上ほぼ気づかない短さ）
    window.setTimeout(run, 50);
  }

  function cssVar(name) {
    return getComputedStyle(html).getPropertyValue(name).trim();
  }

  function msFromVar(name) {
    var raw = cssVar(name);
    if (!raw) return 0;
    var value = parseFloat(raw);
    return raw.indexOf("ms") !== -1 ? value : value * 1000;
  }

  /* サイトのTOPページ（"/"）かどうかを判定する。
     Shopifyの実装では、ここをテーマの仕様に合わせて調整する
     （例: ルートURLだけでなく複数の言語/国別ルートを含める等） */
  function isTopUrl(url) {
    return url.pathname === "/" || /\/index\.html?$/.test(url.pathname);
  }

  // PCの独自スクロール領域とスマホのページスクロールを即座に先頭へ戻す。
  function resetScrollToTop() {
    var wrapper = document.querySelector(".page-wrapper");
    if (wrapper) wrapper.scrollTo({top: 0, left: 0, behavior: "instant"});
    window.scrollTo({top: 0, left: 0, behavior: "instant"});

    // scroll-container.js が pageshow で古い位置を復元しないようにする。
    try {
      var state = Object.assign({}, history.state, {scrollTop: 0});
      history.replaceState(state, "");
    } catch (e) {}
  }

  // TOP内のロゴクリック専用。通信せず、白い幕の裏で先頭へ戻す。
  var homeReturnBusy = false;
  function playHomeReturn() {
    if (homeReturnBusy) return;
    if (reduceMotion() || cssVar("--home-return-enabled") !== "1") {
      resetScrollToTop();
      return;
    }

    homeReturnBusy = true;
    var hero = document.querySelector('main[data-template="index"] > :first-child');
    var overlay = document.createElement("div");
    overlay.className = "home-return-overlay";
    overlay.setAttribute("aria-hidden", "true");
    document.body.appendChild(overlay);
    var coverTimer;
    var finishTimer;
    var finished = false;

    function finish() {
      if (finished) return;
      finished = true;
      window.clearTimeout(coverTimer);
      window.clearTimeout(finishTimer);
      if (hero) hero.classList.remove("home-return-enter");
      overlay.remove();
      homeReturnBusy = false;
      window.removeEventListener("pagehide", finish);
    }
    window.addEventListener("pagehide", finish);

    nextFrame(function () {
      if (finished) return;
      overlay.classList.add("is-covered");
      coverTimer = window.setTimeout(function () {
        if (finished) return;
        resetScrollToTop();
        if (hero) hero.classList.add("home-return-enter");
        nextFrame(function () {
          if (finished) return;
          overlay.classList.add("is-revealing");
          finishTimer = window.setTimeout(finish, 460);
        });
      }, 360);
    });
  }

/* 先頭(0)へブラウザ標準のスムーズスクロールで戻す。
   window.scrollTo(0, 0) の瞬間移動だと、幕が開いて中身が見えた
   直後に画面がガクッと先頭へ飛ぶように見えてしまうため。 */
function smoothScrollToTop() {
  window.scrollTo({top: 0, left: 0, behavior: "smooth"});
}

  /* ---------------------------------------------------------
     [条件1] 表示・リロード時のシンプルな幕
     （押し出すコンテンツが無いので、幕だけが下へ流れて消える）
     --------------------------------------------------------- */
  function playSimpleCurtain(curtain, forceTopScroll) {
    if (!curtain) return;

    curtain.hidden = false;
    curtain.style.transition = "none";
    curtain.style.transform = "translateY(0%)";

    nextFrame(function () {
      window.setTimeout(function () {
        curtain.style.transition =
          "transform " + cssVar("--intro-duration") + " " + cssVar("--ease");
        curtain.style.transform = "translateY(100%)";
      }, msFromVar("--intro-hold"));
    });

    var finished = false;
    var finish = function () {
      if (finished) return;
      finished = true;
      if (forceTopScroll) {
        // ブラウザのスクロール位置自動復元が、この同期スクリプトより
        // 後のタイミングで効いてしまうケースがあるため、幕が完全に
        // 開き切る直前（＝まだ画面を覆っている間）に確実に先頭へ
        // 強制する。ここでの変更はユーザーには見えない。
        smoothScrollToTop();
      }
      curtain.hidden = true;
      curtain.style.transition = "";
      curtain.style.transform = "";
    };
    curtain.addEventListener("transitionend", finish, {once: true});
    window.setTimeout(
      finish,
      msFromVar("--intro-hold") + msFromVar("--intro-duration") + 400
    );
  }

  /* ---------------------------------------------------------
     [条件2-b] TOPページの読み込み直後：入場演出
     html.is-curtain-enter は、FOUC防止のため head内の同期
     スクリプトで既に付与されている前提（.js-page-content が
     最初から translateY(-100%) の状態でCSS上は静止している）
     --------------------------------------------------------- */
  function playEnterCurtain(curtain, content) {
    if (!curtain) return;

    curtain.hidden = false;
    curtain.style.transition = "none";
    curtain.style.transform = "translateY(0%)";

    if (content) {
      content.style.transition = "none";
      content.style.transform = "translateY(-100vh)";
    }

    var dur = cssVar("--push-duration") + " " + cssVar("--ease");

    nextFrame(function () {
      window.setTimeout(function () {
        curtain.style.transition = "transform " + dur;
        curtain.style.transform = "translateY(100%)";
        if (content) {
          content.style.transition = "transform " + dur;
          content.style.transform = "translateY(0%)";
        }
      }, msFromVar("--intro-hold"));
    });

    var finished = false;
    var finish = function () {
      if (finished) return;
      finished = true;

      curtain.hidden = true;
      curtain.style.transition = "";
      curtain.style.transform = "";

      if (content) {
        content.style.transition = "";
        content.style.transform = "";
      }
      html.classList.remove("is-curtain-enter");
    };
    curtain.addEventListener("transitionend", finish, {once: true});
    window.setTimeout(
      finish,
      msFromVar("--intro-hold") + msFromVar("--push-duration") + 400
    );
  }

  /* ---------------------------------------------------------
     [条件2-a] 下層 → TOP へ移動する瞬間：退場演出
     幕が上から入ってきて画面を覆い切ったところで、実際に遷移する。
     --------------------------------------------------------- */
  function playExitThenNavigate(curtain, content, url) {
    safeSet(ENTER_FLAG, "1");

    if (!curtain) {
      window.location.href = url;
      return;
    }

    html.classList.remove("no-intro");
    html.classList.add("is-curtain-exit");
    curtain.hidden = false;
    curtain.style.transition = "none";
    curtain.style.transform = "translateY(-100%)";

    if (content) {
      content.style.transition = "none";
      content.style.transform = "translateY(0%)";
    }

    var dur = cssVar("--push-duration") + " " + cssVar("--ease");

    nextFrame(function () {
      curtain.style.transition = "transform " + dur;
      curtain.style.transform = "translateY(0%)"; // 幕が画面を覆い切る
      if (content) {
        content.style.transition = "transform " + dur;
        content.style.transform = "translateY(100%)";
      }
    });

    var navigated = false;
    var go = function () {
      if (navigated) return;
      navigated = true;
      window.location.href = url;
    };
    // 幕が画面を覆い切ったタイミングで実際に遷移させる
    // （ダブルrAFで1〜2フレーム分遅れる可能性を見込んで少し余裕を持たせる）
    window.setTimeout(go, msFromVar("--push-duration") + 80);
  }

  document.addEventListener("DOMContentLoaded", function () {
    var curtain = document.querySelector(".curtain");
    var content = document.querySelector(".js-page-content");

    var suppressOnLoad = !!window.__introSuppressPage;

    var skip = safeGet(SKIP_FLAG) === "1";
    var enter = safeGet(ENTER_FLAG) === "1";
    var forceTop = safeGet(FORCE_TOP_FLAG) === "1";

    safeSet(SKIP_FLAG, null);
    safeSet(ENTER_FLAG, null);
    safeSet(FORCE_TOP_FLAG, null);

    if (suppressOnLoad || reduceMotion()) {
      if (curtain) curtain.hidden = true;
      html.classList.remove("is-curtain-enter");
      if (forceTop) resetScrollToTop();
    } else if (skip) {
      if (curtain) curtain.hidden = true;
      if (forceTop) resetScrollToTop();
    } else if (enter) {
      playEnterCurtain(curtain, content);
    } else {
      playSimpleCurtain(curtain, forceTop);
    }
    // 幕がまだ表示されている間に、画像読み込み等で発生しうる
    // ブラウザ側の遅延スクロール復元にも念のため対処しておく
    if (forceTop) {
      window.addEventListener(
        "load",
        function () {
          resetScrollToTop();
        },
        {once: true}
      );
    }

    // サイト全体の内部リンクをクリック委譲で監視する
    document.addEventListener("click", function (e) {
      if (
        e.defaultPrevented ||
        e.button !== 0 ||
        e.metaKey ||
        e.ctrlKey ||
        e.shiftKey ||
        e.altKey
      ) {
        return; // 中クリック・修飾キー押下・別ハンドラでの処理済みは対象外
      }

      var link = e.target.closest("a[href]");
      if (!link) return;
      if (link.target && link.target !== "" && link.target !== "_self") {
        return; // 別タブ・別ウィンドウで開くリンクは対象外
      }

      var url;
      try {
        url = new URL(link.href, window.location.href);
      } catch (err) {
        return;
      }

      if (url.origin !== window.location.origin) return; // 外部リンクは対象外
      var forceCurtain = link.hasAttribute("data-force-curtain");

      if (forceCurtain && isTopUrl(window.location) && isTopUrl(url)) {
        // TOP内のロゴクリックは、再読み込みせず短いフェードで先頭へ戻す
        e.preventDefault();

        safeSet(SKIP_FLAG, null);
        safeSet(ENTER_FLAG, null);
        safeSet(FORCE_TOP_FLAG, null);

        playHomeReturn();
        return;
      }

      if (
        !forceCurtain &&
        url.pathname === window.location.pathname &&
        url.search === window.location.search
      ) {
        return; // 同一ページ内（ページ内アンカー等）は対象外
      }

      if (isTopUrl(url)) {
        if (reduceMotion()) return; // 通常のリンク動作に任せる
        e.preventDefault();
        playExitThenNavigate(curtain, content, link.href);
      } else {
        // [条件2] それ以外の内部遷移はアニメーションなし。
        // 遷移先ではイントロも出さないようフラグだけ立てて、
        // リンク自体は普通に遷移させる（preventDefaultしない）。
        safeSet(SKIP_FLAG, "1");
      }
    });
  });
})();

/* ブラウザバック（戻る）でページが復元された時に、カーテンが止まったままになるのを防ぐ */
(function () {
  'use strict';
  window.addEventListener('pageshow', function (event) {
    // persisted = true：ページを読み込み直さず、戻る前の状態のまま復元された時（iPhone Safari で起きやすい）
    if (!event.persisted) return;

    var html = document.documentElement;
    var curtain = document.querySelector('.curtain');
    var content = document.querySelector('.js-page-content');

    // ページ移動の途中の状態を解除する
    html.classList.remove('is-curtain-exit', 'is-curtain-enter');
    try { sessionStorage.removeItem('curtainEnterNext'); } catch (e) {}
    if (content) {
      content.style.transition = 'none';
      content.style.transform = '';
    }

    if (!curtain || curtain.hidden) return;

    // カーテンを画面いっぱいの位置から、下へ動かして消す（リロード時と同じ動き）
    var cs = getComputedStyle(html);
    var dur = cs.getPropertyValue('--intro-duration').trim() || '1000ms';
    var ease = cs.getPropertyValue('--ease').trim() || 'ease';

    curtain.style.transition = 'none';
    curtain.style.transform = 'translateY(0%)';
    void curtain.offsetHeight;

    var done = false;
    function finish() {
      if (done) return;
      done = true;
      curtain.hidden = true;
      curtain.style.transition = '';
      curtain.style.transform = '';
      if (content) content.style.transition = '';
    }
    curtain.addEventListener('transitionend', function onEnd(e) {
      if (e.target !== curtain) return;
      curtain.removeEventListener('transitionend', onEnd);
      finish();
    });
    // 念のため：transitionend が来なくても必ず消す
    setTimeout(finish, (parseFloat(dur) || 1000) * (dur.indexOf('ms') !== -1 ? 1 : 1000) + 400);

    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        curtain.style.transition = 'transform ' + dur + ' ' + ease;
        curtain.style.transform = 'translateY(100%)';
      });
    });
  });
})();